import React, {useEffect, useState} from "react";
import {Button, Form, Header, Input, Message, Segment} from "semantic-ui-react";
import {toast} from "react-toastify";
import {ToasterConfig} from "../../../config/ToasterConfig";
import useFleetTypeService from "../../../services/backoffice/hooks/useFleetTypeService";
import {UpdateFleetTypeRequest} from "../../../models/requests/create-fleet-type-request";

interface FleetTypeProps {
    isModal?: boolean;
    open?: boolean;
    onClose?: () => void;
    fleetTypeId?: string;
}

const FleetTypeForm: React.FC<FleetTypeProps> = ({fleetTypeId, onClose}) => {
    const [type, setType] = useState<Partial<UpdateFleetTypeRequest>>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const {createFleetType, updateFleetType, getFleetTypeById} = useFleetTypeService();

    useEffect(() => {
        if (fleetTypeId) {
            setLoading(true);
            getFleetTypeById(fleetTypeId)
                .then((response) => {
                    setType(response);
                })
                .catch((error) => {
                    setError("Erro ao carregar tipo de Frota.");
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [fleetTypeId]);

    const handleSubmit = async () => {
        try {
            if (!type.code || !type.name) {
                setError("Todos os campos são obrigatórios.");
                return;
            }
            
            setLoading(true);
            if (fleetTypeId) {
                await updateFleetType(fleetTypeId, {name: type.name, code: type.code});
                toast.success("Tipo de Frota atualizado com sucesso", ToasterConfig.success);
            } else {
                await createFleetType({name: type.name, code: type.code});
                toast.success("Tipo de Frota criado com sucesso", ToasterConfig.success);
            }
            if (onClose) onClose();
        } catch (error) {
            setError("Erro ao salvar tipo de Frota.");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = event.target;
        setType((prevType) => ({
            ...prevType,
            [name]: value,
        }));
    };

    const handleClear = () => {
        setType({});
    };

    const isSubmitDisabled = !type.code || !type.name;

    return (
        <Segment raised style={{maxWidth: "600px", margin: "5px auto", padding: "20px"}}>
            <Header
                as="h3"
                style={{
                    background: "#49ab3a",
                    color: "#fff",
                    display: "flex",
                    alignItems: "left",
                    justifyContent: "flex-start",
                    padding: "10px",
                    margin: "0",
                    borderRadius: "5px 5px 0 0",
                }}
            >
                {fleetTypeId ? "Editar Tipo de Frota" : "Cadastro de Tipos de Frota"}
            </Header>
            <Form onSubmit={handleSubmit}>
                <div style={{
                    border: "2px solid #d4d4d5",
                    borderRadius: "4px",
                    padding: "10px",
                    margin: "10px 0"
                }}>
                    <Form.Group widths="equal">
                        <Form.Field required>
                            <label>Código</label>
                            <Input
                                name="code"
                                placeholder="Digite o código"
                                onChange={handleInputChange}
                                value={type.code || ""}
                                disabled={loading}
                                style={{padding: "10px", width: "100%"}}
                                fluid
                                error={isSubmitDisabled && !type.code}
                            />
                        </Form.Field>
                        <Form.Field required>
                            <label>Tipo de Frota</label>
                            <Input
                                name="name"
                                placeholder="Digite o tipo"
                                onChange={handleInputChange}
                                value={type.name || ""}
                                disabled={loading}
                                style={{padding: "10px", width: "100%"}}
                                fluid
                                error={isSubmitDisabled && !type.name}
                            />
                        </Form.Field>
                    </Form.Group>
                </div>
                <div style={{textAlign: "right"}}>
                    <Button color="green" type="submit" disabled={isSubmitDisabled || loading}>
                        {fleetTypeId ? "Atualizar" : "Salvar"}
                    </Button>
                    <Button type="button" onClick={handleClear} disabled={loading}>
                        Limpar
                    </Button>
                    <Button type="button" onClick={onClose} disabled={loading}>
                        Cancelar
                    </Button>
                </div>
                {error && <Message negative>{error}</Message>}
            </Form>
        </Segment>
    );
};

export default FleetTypeForm;
